import { Injector } from '@angular/core';
import { AuthService, CheckAuthenticationStateFn, ConfigStateService } from '@abp/ng.core';
import { clearOAuthStorage } from '@abp/ng.oauth';
import { OAuthService } from 'angular-oauth2-oidc';

/**
 * Replacement for ABP's default `checkAccessToken` self-heal
 * (provided via `CHECK_AUTHENTICATION_STATE_FN_KEY`).
 *
 * It runs once during app bootstrap, right after the `application-configuration`
 * call resolves. ABP's stock version only clears the OAuth storage when the
 * browser still holds a clock-valid access token while the backend resolved an
 * *anonymous* user — but it never navigates, so the SPA stays on a degraded,
 * half-logged-in screen until a manual reload.
 *
 * That degraded state is exactly what happens on the on-premise Docker
 * deployment after a container restart rotates the (ephemeral) OpenIddict
 * signing/encryption keys: the 8h access token kept in localStorage is still
 * "valid by the browser clock" (so route guards let the user in), yet every
 * backend request is now anonymous — SignalR won't connect, `currentUser.id` is
 * undefined, and logout silently fails.
 *
 * This version additionally forces a clean re-login (auth-code-flow redirect) so
 * the user lands on the login page instead of a broken session.
 */
export const checkAuthenticationStateOrLogin: CheckAuthenticationStateFn = (injector: Injector) => {
  const configState = injector.get(ConfigStateService);
  const oAuth = injector.get(OAuthService);

  const hasClockValidToken = oAuth.hasValidAccessToken();
  const hasBackendUser = !!configState.getDeep('currentUser.id');

  if (hasClockValidToken && !hasBackendUser) {
    // The token looks valid to the browser but the backend does not recognise
    // the user (e.g. server keys rotated on restart). Drop the stale token and
    // send the user through a fresh login instead of leaving them stuck.
    clearOAuthStorage();
    injector.get(AuthService).navigateToLogin();
  }
};
